<script lang="ts">
    import {newPresentationOptions} from "../store/presentationOptions";
    import { onMount } from 'svelte';
    import {derived} from "svelte/store";
    import {createNewPerson, createNewPresentation} from '../actions/newPresentation';
    import {tap} from "rxjs";
    import {generateTopic} from "../actions/generateTopic";
    import {suggestions} from '../store/suggestions.js';
    import {getSuggestions} from '../actions/getSuggestions';

    const defaultPersonality = 'honestPresenter';
    const defaultSpeaker = 'Justin';

    const speakerOptions = derived(newPresentationOptions, (options) => options.speakers);
    const personalityOptions = derived(newPresentationOptions, (options) => options.personalities);
    const languageOptions = derived(newPresentationOptions, (options) => options.languages);
    const generatorOptions = derived(newPresentationOptions, (options) => options.generators);
    const textGeneratorOptions = derived(newPresentationOptions, (options) => options.textGenerators);
    const slideOptions = derived(newPresentationOptions, (options) => options.slides);

    enum GeneratorStatus {
        WAITING,
        GENERATING,
        ERROR,
        ABORT,
        DONE
    }

    let selectedSuggestion;
    let suggestionModal: HTMLDialogElement;

    let status: GeneratorStatus = GeneratorStatus.WAITING;
    let topic: string;
    let language: string;
    let numberOfSlides: string;
    let speaker: string;
    let generator: string;
    let textGenerator: string;
    let generatorMessage: string = '';
    let flaggingInformation: string = '';
    let personality: string = 'no-personality';

    let personName: string;
    let personDescription: string;
    let narrationStyle: string;
    let person_voice: string;
    let personImageDescription: string;
    let image: string;
    let personGenerator: string = "hardcoded-person";

    let contentModeration: boolean = true;

    let showPersonData: boolean = false;


    $: submitDisabled = !topic || topic.length < 3
        || !personDescription || !personImageDescription || !personName || !speaker;
    $: custom_selected = (personality === "custom")

    async function generateNewTopic(event: Event) {
        event.preventDefault();
        event.stopPropagation();

        console.log("Generating topic");
        topic = await generateTopic();
        console.log(`Generated topic "${topic}"`);
    }

    function generatePerson(event: Event) {
        event.preventDefault();
        event.stopPropagation();

        status = GeneratorStatus.GENERATING;

        createNewPerson({
            name: personName,
            personDescription: personDescription,
            imageDescription: personImageDescription,
            speaker: speaker,
            imageGeneratorName: generator,
            textGeneratorName: textGenerator
        }).pipe(tap(msg => {
            console.log(msg);
        })).subscribe({
            next(message) {
                if (message.type === 'progress') {
                    generatorMessage = message.message ?? '';
                }
                if (message.type === 'error') {
                    generatorMessage = message.message ?? '';
                    status = GeneratorStatus.ERROR;
                }
                if (message.type === 'abort') {
                    generatorMessage = message.message ?? '';
                    flaggingInformation = message.flaggingInformation;
                    status = GeneratorStatus.ABORT;
                }
                if (message.type == "created-person") {
                    status = GeneratorStatus.WAITING;
                    personName = message.payload.name;
                    personDescription = message.payload.personDescription;
                    speaker = message.payload.speaker;
                    personImageDescription = message.payload.imageDescription;

                    image = "data:image/jpeg;base64," + message.payload.image;
                }
            },
            error(err) {
                console.error(err);
                generatorMessage = 'technical error';
                status = GeneratorStatus.ERROR;
            },
            complete() {
            }
        });
    }

    async function selectSuggestion(event: Event) {
        event.preventDefault();
        event.stopPropagation();

        if (suggestionModal) {
            await getSuggestions();
            suggestionModal.showModal();
        }
    }

    function useSelection(event: Event) {
        event.preventDefault();
        event.stopPropagation();

        topic = selectedSuggestion;

        if (suggestionModal) {
            suggestionModal.close();
        }
    }

    function reset() {
        status = GeneratorStatus.WAITING;
        generatorMessage = "";
    }

    function personalityChanged(event: any) {
        personality = event.target.value;
        selectPersonality(personality);
    }

    function selectPersonality(personality: string) {
        if (personality !== 'custom') {
            const person = $personalityOptions.find(entry => entry.value === personality);
            personName = person.name || '';
            personDescription = person.personDescription || '';
            personImageDescription = person.imageDescription || '';
            speaker = person.speaker || 'Joanna';
            image = `/img/presenters/${person.value}.jpg`;
        } else {
            personName = '';
            personDescription = '';
            personImageDescription = '';
            speaker = defaultSpeaker;
            image = `/img/presenters/custom.jpg`;
        }
    }

    function clearPerson(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        personName = "";
        personDescription = "";
        personImageDescription = "";
        image = "";
    }

    function changePersonDataVisibility(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        showPersonData = !showPersonData;
    }

    function submit(event: Event) {
        event.preventDefault();
        event.stopPropagation();

        status = GeneratorStatus.GENERATING;

        language = $speakerOptions.find(entry => entry.value === speaker).language;
        createNewPresentation({
            title: topic,
            numberOfSlides: Number.parseInt(numberOfSlides),
            narrationStyle: narrationStyle,
            textGeneratorName: textGenerator,
            imageGeneratorName: generator,
            language: language,
            contentModeration: contentModeration,
            person: {
                name: personName,
                personDescription: personDescription,
                imageDescription: personImageDescription,
                speaker: speaker,
                image: image
            }
        }).pipe(tap(msg => {
            console.log(msg);
        })).subscribe({
            next(message) {
                if (message.type === 'progress') {
                    generatorMessage = message.message ?? '';
                }
                if (message.type === 'error') {
                    generatorMessage = message.message ?? '';
                    status = GeneratorStatus.ERROR;
                }
                if (message.type === 'abort') {
                    generatorMessage = message.message ?? '';
                    flaggingInformation = message.flaggingInformation;
                    status = GeneratorStatus.ABORT;
                }
                if (message.type === 'created-presentation') {
                    status = GeneratorStatus.DONE;
                    window.location.href = message.url;
                }
            },
            error(err) {
                console.error(err);
                generatorMessage = 'technical error';
                status = GeneratorStatus.ERROR;
            },
            complete() {
            }
        });
    }

    onMount(() => {
        personality = defaultPersonality;
        selectPersonality(personality);
    });
</script>

<div class="new-presentation-container">
    <h2>New Presentation</h2>
    <br>
    {#if status === GeneratorStatus.GENERATING}
        <div class="loading">
            <div class="pl pl-leapfrog"></div>
            <div class="loading-text">generating...</div>
            <div class="message">{generatorMessage}</div>
        </div>
    {:else if status === GeneratorStatus.ERROR}
        <div>
            Generation has failed<br/>
            <div class="error">{generatorMessage}</div>
            <button on:click={reset}>try again?</button>
        </div>
    {:else if status === GeneratorStatus.ABORT}
        <div>
            Content too spicy!<br/>
            <div class="error">{flaggingInformation}</div>
            <button on:click={reset}>try again?</button>
        </div>
    {:else if status === GeneratorStatus.DONE}
        <div>
            Presentation is done, redirecting...
        </div>
    {:else}
        <div class="left">
            <div class="left-child" style="min-width: 600px;">
                <dialog bind:this={suggestionModal}>
                    <form on:submit={useSelection}>
                        <select bind:value={selectedSuggestion}>
                            {#each $suggestions as suggestion}
                                <option value={suggestion.suggestion}>{suggestion.suggestion}</option>
                            {/each}
                        </select>
                        <input type='submit' value='use'/>
                    </form>
                </dialog>
                <form class="presentation-form">
                    <label for="topic">Topic</label>
                    <input type="text" id="topic" bind:value={topic}/>
                    <div></div>
                    <div class="two-buttons">
                        <button class="split" on:click={generateNewTopic}>Generate topic</button>
                        <button class="split" on:click={selectSuggestion}>Use a suggestion</button>
                    </div>

                    <label for="slide">Number of Slides</label>
                    <select id="slide" bind:value={numberOfSlides}>
                        {#each $slideOptions as slideOption}
                            <option value="{slideOption.value}">{slideOption.label}</option>
                        {/each}
                    </select>

                    <label for='narration-style'>Narration Style</label>
                    <textarea type="text" class="text-field" id="narration-style"
                              bind:value={narrationStyle}/>

                    <label for="personality">Personality</label>
                    <select id="personality" bind:value={personality} on:input={personalityChanged}>
                        {#each $personalityOptions as personalityOption}
                            <option value="{personalityOption.value}">{personalityOption.label}</option>
                        {/each}
                        <option value="custom">Custom Personality</option>
                    </select>

                    <div></div>
                    <button class="split" on:click={changePersonDataVisibility}>Show Person Data</button>

                    {#if showPersonData}
                        <hr/>

                        <label for="profile-picture">Profile Picture</label>
                        <img id="profile-picture" class="profile-picture" src="{image}" alt='profile picture'/>

                        <label for="person-name">Name</label>
                        <input type="text" id="person-name" bind:value={personName}/>

                        <label for="person-description">Person Description</label>
                        <textarea type="text" class="text-field" id="person-description"
                                  bind:value={personDescription}/>

                        <label for="speaker">Speaker</label>
                        <select id="speaker" bind:value={speaker} class="speaker-dropdown">
                            {#each $speakerOptions as speakerOption}
                                <option value="{speakerOption.value}">{speakerOption.label}</option>
                            {/each}
                        </select>

                        <label for="profile-picture-description">Image Description</label>
                        <textarea type="text" class="text-field" id="profile-picture-description"
                                  bind:value={personImageDescription}/>

                        <div></div>
                        <div class="two-buttons">
                            <button class="split" on:click={generatePerson}>
                                Fill in blanks
                            </button>
                            <button class="split" on:click={clearPerson}>
                                Clear Person
                            </button>
                        </div>

                        <hr/>
                    {/if}

                    <label for="generator">Image Generator</label>
                    <select id="generator" bind:value={generator}>
                        {#each $generatorOptions as generatorOption}
                            <option value="{generatorOption.value}">{generatorOption.label}</option>
                        {/each}
                    </select>

                    <label for="generator">Text Generator</label>
                    <select id="textGenerator" bind:value={textGenerator}>
                        {#each $textGeneratorOptions as textGeneratorOption}
                            <option value="{textGeneratorOption.value}">{textGeneratorOption.label}</option>
                        {/each}
                    </select>

                    <label for="content-moderation">Content</label>
                    <div>
                        <input type="checkbox" id="content-moderation" bind:checked={contentModeration} />
                        Moderate content
                    </div>

                    <input type="submit" value="Create Presentation" class="submit-button"
                           disabled="{submitDisabled}" on:click={submit}/>
                </form>
            </div>
        </div>

    {/if}
</div>

<style>
    hr {
        grid-column-start: 1;
        grid-column-end: 3;
    }

    .left {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .profile-picture {
        width: 150px;
        height: 150px;
    }

    .speaker-dropdown {
        height: 30px;
    }

    .text-field {
        height: 100px;
        resize: none;
    }

    .error {
        color: red;
        font-weight: bold;
    }

    .two-buttons {
        display: flex;
        flex-direction: row;
        justify-content: stretch;
        align-items: baseline;
    }

    button.split {
        flex-grow: 1;
        margin-left: 0.5%;
    }

    .submit-button {
        grid-column-start: 1;
        grid-column-end: 3;

        margin-top: 30px;
        margin-bottom: 30px;
    }

    .new-presentation-container {
    }

    .presentation-form {
        display: grid;
        grid-template-columns: 30% 70%;
        gap: 0.5rem;
    }

    .presentation-form label {
        text-align: left;
    }

    @media (max-width: 400px) {
        .presentation-form {
            grid-template-columns: 1fr;
        }

        .presentation-form label {
            text-align: left;
        }
    }

    .loading {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    /** Global preloader styles **/
    .pl, .pl:before, .pl:after {
        animation-duration: 2s;
        animation-timing-function: linear;
        animation-iteration-count: infinite;
    }

    .pl {
        margin-top: 5rem;
        margin-bottom: 2rem;
        position: relative;
        width: 6rem;
        height: 6rem;
    }

    .pl:before, .pl:after {
        background: currentColor;
        content: "";
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 50%;
        transform-origin: 50% 100%;
        clip-path: polygon(0 0, 100% 0, 50% 100%);
        -webkit-clip-path: polygon(0 0, 100% 0, 50% 100%);
    }

    /* Leap Frog */
    .pl-leapfrog:before, .pl-leapfrog:after {
        clip-path: polygon(0 0, 100% 0, 0 100%);
        -webkit-clip-path: polygon(0 0, 100% 0, 0 100%);
        top: 50%;
        left: 50%;
        width: 70.71%;
        height: 70.71%;
        transform-origin: 0 0;
    }

    .pl-leapfrog:before {
        animation-name: leapFrogA;
    }

    .pl-leapfrog:after {
        animation-name: leapFrogB;
        background: #a3a3a3;
    }

    @keyframes leapFrogA {
        from {
            transform: rotateZ(-135deg) rotateY(0deg) rotateX(0deg);
            z-index: 0
        }
        12.5% {
            transform: rotateZ(-135deg) rotateY(-180deg) rotateX(0deg);
            z-index: 1
        }
        25%, 50% {
            transform: rotateZ(-135deg) rotateY(-180deg) rotateX(-180deg);
            z-index: 0
        }
        62.5% {
            transform: rotateZ(-135deg) rotateY(0deg) rotateX(-180deg);
            z-index: 1
        }
        75%, to {
            transform: rotateZ(-135deg) rotateY(0deg) rotateX(-360deg);
            z-index: 0
        }
    }

    @keyframes leapFrogB {
        from, 25% {
            transform: rotateZ(-45deg) rotateY(0deg) rotateX(0deg);
            z-index: 0
        }
        37.5% {
            transform: rotateZ(-45deg) rotateY(-180deg) rotateX(0deg);
            z-index: 1
        }
        50%, 75% {
            transform: rotateZ(-45deg) rotateY(-180deg) rotateX(-180deg);
            z-index: 0
        }
        87.5% {
            transform: rotateZ(-45deg) rotateY(0deg) rotateX(-180deg);
            z-index: 1
        }
        to {
            transform: rotateZ(-45deg) rotateY(0deg) rotateX(-360deg);
            z-index: 0
        }
    }

    /** Media queries **/
    @media (prefers-color-scheme: dark) {
        .pl-leapfrog:after {
            background: #575757;
        }
    }
</style>
